import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { useAuth } from '../../hooks/useAuth';
import { canManageVenue } from '@wineryxplorer/shared';
import type { Media, Venue, PriceRange, PrimaryAtmosphere, Amenity, ReservationPolicy } from '@wineryxplorer/shared';
import { AiGeneratedBadge } from '../../components/ui/AiGeneratedBadge';
import { HeroCarousel } from '../../components/HeroCarousel/HeroCarousel';
import { OperatingHours } from '../../components/OperatingHours';
import { PageContainer } from '../../components/PageContainer';
import { ContentContainer } from '../../components/ContentContainer';
import { ImageModal } from '../../components/ui/ImageModal';
import { TastingExperiences } from '../../components/TastingExperiences';
import { UpcomingEvents } from '../../components/UpcomingEvents';
import { VenueReviews } from '../../components/VenueReviews';
import { WishlistMatchBadge } from '../../components/WishlistMatchBadge';
import { WineryHeader } from './WineryHeader';
import { WineryVenueDetails } from './WineryVenueDetails';
import { WineryGallery } from './WineryGallery';
import { WineryContactLocation } from './WineryContactLocation';
import { ReportErrorLink } from '../../components/ReportErrorLink';

export const WineryDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const { data: venue, isPending, error } = trpc.venue.getById.useQuery(
    { id: id || '' },
    { enabled: Boolean(id) }
  );

  const { data: venueMedia } = trpc.media.listByEntity.useQuery(
    // limit=100 (the max the endpoint allows) — default is 20, which silently
    // drops hero images on venues with deep galleries since heros are typically
    // the oldest media records and fall off the first page.
    { entityType: 'venue', entityId: id || '', limit: 100 },
    { enabled: Boolean(id) }
  );

  const wineCountQuery = trpc.wine.listByVenue.useQuery(
    { venueId: id ?? '', limit: 1 },
    { enabled: Boolean(id) }
  );
  const wineCount = wineCountQuery.data?.total ?? 0;

  // Cast media array to proper type since tRPC returns unknown[]
  const mediaList = (venueMedia?.items ?? []) as Media[];

  const getHeroImages = (): Media[] => {
    if (!mediaList.length || !venue?.heroImageIds) {
      return [];
    }
    return mediaList.filter((media) => venue.heroImageIds.includes(media.id));
  };

  const getGalleryImages = (): Media[] => {
    if (!mediaList.length || !venue?.imageIds) {
      return [];
    }
    return mediaList.filter((media) => venue.imageIds.includes(media.id));
  };

  const handleImageClick = (imageIndex: number) => {
    setSelectedImageIndex(imageIndex);
    setModalOpen(true);
  };

  if (isPending) {
    return (
      <div className="px-0 md:px-4 py-8 max-w-4xl 2xl:max-w-6xl mx-auto">
        <PageContainer>
          <p className="text-gray-600">Loading winery...</p>
        </PageContainer>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl 2xl:max-w-6xl mx-auto">
        <PageContainer className="mt-2">
          <p className="text-error">Error loading winery: {error.message}</p>
        </PageContainer>
      </div>
    );
  }

  const heroImagesData = getHeroImages();
  const galleryImages = getGalleryImages();
  const canManage = user ? canManageVenue(venue as unknown as Venue, user.role, user.id) : false;

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl 2xl:max-w-6xl mx-auto">
      <PageContainer className="space-y-10 md:space-y-14 mt-2">
        <div className="-mx-3 -mt-3 sm:overflow-hidden sm:rounded-t-lg">
          <HeroCarousel images={heroImagesData} venueName={venue.name} venueType={venue.venueType} />
        </div>

        {/* Header with Logo */}
        <WineryHeader
          venue={venue}
          mediaList={mediaList}
          canManage={canManage}
          onManageClick={() => navigate(`/wineries/${id}/manage`)}
        />

        {/* Wishlist match badge */}
        {id && (
          <ContentContainer>
            <WishlistMatchBadge venueId={id} />
          </ContentContainer>
        )}

        {/* Description */}
        <ContentContainer>
          {venue.description ? (
            <div className="max-w-prose">
              <p className="text-gray-700">{venue.description}</p>
              {(venue as unknown as { descriptionSource?: string | null }).descriptionSource === 'ai_generated' && (
                <AiGeneratedBadge
                  className="mt-2"
                  title="This description was generated by AI from public sources and has not been reviewed by the venue."
                />
              )}
            </div>
          ) : (
            <p className="text-gray-400 italic">Unknown</p>
          )}
        </ContentContainer>

        {/* Venue Enhancement Fields */}
        <WineryVenueDetails
          priceRange={venue.priceRange as PriceRange}
          primaryAtmosphere={venue.primaryAtmosphere as PrimaryAtmosphere | undefined}
          amenities={venue.amenities as Amenity[]}
          reservationPolicy={(venue as { reservationPolicy?: ReservationPolicy }).reservationPolicy}
        />

        {/* Operating Hours */}
        <ContentContainer>
          {venue.operatingHours ? (
            <OperatingHours
              hours={venue.operatingHours}
              notes={venue.operatingHoursNotes ?? undefined}
              validated={(venue as unknown as { operatingHoursValidated?: boolean }).operatingHoursValidated === true}
            />
          ) : (
            <>
              <h2 className="font-sans text-xs font-semibold uppercase tracking-widest text-gray-500 mb-3">Operating Hours</h2>
              <p className="text-gray-400 italic">Unknown</p>
            </>
          )}
        </ContentContainer>

        {/* Gallery Images */}
        <WineryGallery
          imageIds={venue.imageIds}
          galleryImages={galleryImages}
          onImageClick={handleImageClick}
        />

        {/* Food Information */}
        {(venue.foodAvailableForPurchase || venue.foodDescription) && (
          <ContentContainer>
            <h2 className="font-sans text-xs font-semibold uppercase tracking-widest text-gray-500 mb-3">Food</h2>
            <div className="space-y-2 text-gray-700 max-w-prose">
              {typeof venue.foodAvailableForPurchase === 'boolean' && (
                <div>
                  <span className="font-medium">Food Available:</span>{' '}
                  {venue.foodAvailableForPurchase ? 'Yes' : 'No'}
                </div>
              )}
              {venue.foodDescription && (
                <div>
                  <span className="font-medium">Food Description:</span>{' '}
                  {venue.foodDescription}
                </div>
              )}
            </div>
          </ContentContainer>
        )}

        {/* Contact & Location */}
        <WineryContactLocation venue={venue} />

        {/* Tasting Experiences */}
        <TastingExperiences venueId={id || ''} />

        {wineCount > 0 && (
          <ContentContainer>
            <Link
              to={`/wineries/${id}/wines`}
              className="inline-flex items-center text-primary-700 hover:text-primary-800 font-sans text-sm"
            >
              View full wine catalog ({wineCount} {wineCount === 1 ? 'wine' : 'wines'}) →
            </Link>
          </ContentContainer>
        )}

        {/* Upcoming Events */}
        <UpcomingEvents venueId={id || ''} />

        {/* Reviews */}
        <VenueReviews
          venueId={id || ''}
          venueName={venue.name}
          isVenueManager={canManage}
        />

        {/* Metadata — only shown to users who can manage this venue (venue admins + site/super admins). Internal, not consumer-facing. */}
        {canManage && (
          <div className="text-sm text-gray-500 pt-4 border-t border-gray-200">
            <div>Created: {new Date(venue.createdAt).toLocaleDateString()}</div>
            <div>Last updated: {new Date(venue.updatedAt).toLocaleDateString()}</div>
          </div>
        )}

        <ContentContainer>
          <div className="pt-4 border-t border-gray-200">
            <ReportErrorLink entityType="venue" entityId={venue._id} entityName={venue.name} />
          </div>
        </ContentContainer>
      </PageContainer>

      {/* Image Modal */}
      <ImageModal
        images={galleryImages}
        initialIndex={selectedImageIndex}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};
